import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  Layer,
  Heading,
  Spinner,
  Text,
  Notification,
} from "grommet";
import axios from "axios";
import API_URL from "../../config/config";
import { SendInvite } from "./SendInvite";
import { Mail, Refresh, Trash, View } from "grommet-icons";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";

const ManageUsers = ({ manager_id }) => {
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [assessmentData, setAssessmentData] = useState([]);
  const [assessmentDetailsMap, setAssessmentDetailsMap] = useState({});
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [viewOpenAssessments, setViewOpenAssessments] = useState(true);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [showResultsPopup, setShowResultsPopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [selfAssessmentCounts, setSelfAssessmentCounts] = useState([]);
  // const [notification, setNotification] = useState(null);
  const [modal, setModal] = useState(null);

  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const manager_user_id = user?.user_table_id;

  const fetchAllData = async () => {
    try {
      setIsLoadingUsers(true);
      // 1. Fetch invited users
      const invitesResponse = await axios.get(
        `${API_URL}/manager/get_invited_users_status/${manager_id}`,
      );
      const invites = invitesResponse.data.invites.reverse();
      setInvitedUsers(invites);

      // 2. Extract user_ids from the invited users
      const user_ids = invites.map((item) => item.user_id);

      // 3. Fetch self-assessment counts using the extracted user_ids
      if (user_ids.length > 0) {
        const assessmentResponse = await axios.post(
          `${API_URL}/manager/get_self_assessment_count`,
          { user_ids },
        );
        setSelfAssessmentCounts(assessmentResponse.data.selfAssessmentDetails);
      }
    } catch (err) {
      if (err.response?.status === 429) return;
      console.error("Error fetching data:", err);
      setError("Failed to fetch data. Please try again.");
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Fetch Assessments Data (now including detailsmap)
  const fetchAssessmentData = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/manager/get_manager_assessments/${manager_id}`,
      );
      // response.data now includes both "assessments" and "detailsmap"
      setAssessmentData(response.data.assessments);
      setAssessmentDetailsMap(response.data.detailsmap || {});
    } catch (error) {
      console.error("Error fetching assessments:", error);
      setError("Failed to fetch assessments. Please try again.");
    }
  };

  // Delete User
  const handleDeleteUser = async (user_id, uuid) => {
    if (!uuid) {
      setError("UUID is missing. Cannot delete user.");
      return;
    }
    setLoading(true);
    setError(""); // Clear any existing errors
    try {
      await axios.delete(
        `${API_URL}/manager/delete_user/${manager_id}/${user_id || "null"}/${uuid}`,
      );
      setRefreshKey((prevKey) => prevKey + 1);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
    } catch (error) {
      if (error.response?.status === 429) return;
      console.error("Error deleting user:", error);
      setError("Failed to delete user. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Refresh Users After Invite
  const handleUserAdded = async () => {
    setInviteSuccess(true);
    setRefreshKey((prevKey) => prevKey + 1);
    setTimeout(() => setInviteSuccess(false), 2000);
  };

  const createTicketAndEmail = async (
    userId,
    type,
    subjectTemplate,
    bodyTemplate
  ) => {
    try {
      // 1. Create ticket
      const response = await fetch('/api/ticket/generate-ticket', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_id: userId, type })
      });

      if (response.status === 429) {
        const retryAfter = response.headers.get('retry-after');
        const retryAt = retryAfter ? Date.now() + parseInt(retryAfter, 10) * 1000 : null;
        window.dispatchEvent(new CustomEvent('app:rate-limited', { detail: { retryAt } }));
        return;
      }

      if (!response.ok) {
        throw new Error(`Status ${response.status}`);
      }

      const { ticket_number } = await response.json();

      // 2. Build subject & body
      const subject = encodeURIComponent(
        `${subjectTemplate} [Ticket: ${ticket_number}]`
      );
      const body = encodeURIComponent(
        `${bodyTemplate}\n\nTicket Number: ${ticket_number}`
      );
      const bcc = encodeURIComponent('hpe.skilladvisor@meteoros.in');

      // 3. Open mailto
      const mailtoURL = `mailto:hpeskill.advisor@hpe.com` +
        `?subject=${subject}` +
        `&bcc=${bcc}` +
        `&body=${body}`;

      window.open(mailtoURL);
    } catch (err) {
      console.error('Unable to generate ticket', err);
      alert('Could not create ticket. Please try again.');
    }
  };

  //   const handleWriteToUs = (email) => {
  //     const subject = encodeURIComponent(
  //       "Request to Remove User from My Team - HPE Digital Skill Advisor",
  //     );
  //     const body = encodeURIComponent(
  //       `Dear Support Team,

  // I hope you're doing well. I would like to request the removal of the user associated with the email ${email} from my team on HPE Digital Skill Advisor.

  // Thank you for your assistance.

  // Best regards`,
  //     );
  //     window.location.href = `mailto: hpe.skilladvisor@meteoros.in?subject=${subject}&body=${body}`;
  //   };

  const handleWriteToUs = (email) => {
    createTicketAndEmail(
      manager_user_id,
      'Remove User From Team',
      'Request to Remove User from My Team',
      `Dear Support Team,\n\nI hope you're doing well. I would like to request the removal of the user associated with the email ${email} from my team on HPE Digital Skill Advisor.\n\nThank you for your assistance.\n\nBest regards.`
    );
  };


  useEffect(() => {
    fetchAllData();
  }, [refreshKey]);

  useEffect(() => {
    fetchAllData();
    fetchAssessmentData();
  }, [refreshKey]);

  // Function to get manager assessments by user and completion status
  const getAssessmentsByUser = (user_id, isComplete) => {
    return assessmentData.filter((assessment) =>
      assessment.users?.some(
        (user) => user.user_id === user_id && user.isComplete === isComplete,
      ),
    );
  };

  // Handlers for Assessment Popup
  const handleViewAssessments = (user) => {
    setSelectedUser(user);
    setShowPopup(true);
    setViewOpenAssessments(true); // default view for open assessments
  };

  const handleSendEmailReminder = async (userId) => {
    try {
      await axios.get(`${API_URL}/manager/send_invitation_reminder/${userId}`);
      setModal({
        message: "Reminder sent successfully!",
        status: "normal",
      });
      // Clear the notification after 3 seconds
      setTimeout(() => {
        setModal(null);
      }, 3000);
    } catch (error) {
      if (error.response?.status === 429) return;
      console.error("Error sending invitation reminder:", error);
      setModal({
        message: "An error occurred while sending the invitation reminder.",
        status: "critical",
      });
      setTimeout(() => {
        setModal(null);
      }, 3000);
    }
  };

  const handleViewResults = (assessment, selectedUser) => {
    
    setSelectedAssessment(assessment);
    navigate(
      `/manager/assessment-quiz-result/${selectedUser.user_id}/${assessment.assessment_id}`,
      { state: selectedUser.name },
    );
  };

  // New handler for self assessment results
  const handleViewSelfResults = (selfAssessment, selectedUser) => {
    
    // For now, we can navigate to a placeholder route; to be updated later.
    navigate(
      `/manager/assessment-quiz-result/${selectedUser.user_id}/${selfAssessment.assessment_id}`,
      { state: selectedUser.name },
    );
  };

  // Render Assessments Table inside Popup
  const renderAssessmentsTable = () => {
    // When open assessments are being viewed, use only manager assessments
    if (viewOpenAssessments) {
      const assessments = getAssessmentsByUser(selectedUser?.user_id, 0);
      return (
        <Table>
          <TableHeader>
            <TableRow>
              <TableCell>
                <Text weight="bold">Assessment Name</Text>
              </TableCell>
              <TableCell>
                <Text weight="bold">Due By</Text>
              </TableCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {assessments.length === 0 ? (
              <TableRow>
                <TableCell colSpan="2" align="center">
                  No Open Assessments
                </TableCell>
              </TableRow>
            ) : (
              assessments.map((assessment) => (
                <TableRow key={assessment.assessment_id}>
                  <TableCell>{assessment.name}</TableCell>
                  <TableCell>
                    {new Date(assessment.due_by).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      );
    } else {
      // Closed assessments: combine manager assessments with self assessments.
      const managerClosedAssessments = getAssessmentsByUser(
        selectedUser?.user_id,
        1,
      );
      const selfAssessments =
        (assessmentDetailsMap && assessmentDetailsMap[selectedUser?.user_id]) ||
        [];

      // Combine both arrays (manager assessments first, then self assessments)
      const hasNoClosedAssessments =
        managerClosedAssessments.length === 0 && selfAssessments.length === 0;

      return (
        <Table>
          <TableHeader>
            <TableRow>
              <TableCell>
                <Text weight="bold">Assessment Name</Text>
              </TableCell>
              <TableCell>
                <Text weight="bold">Assigned By</Text>
              </TableCell>
              <TableCell>
                <Text weight="bold">Due By</Text>
              </TableCell>
              <TableCell>
                <Text weight="bold">Actions</Text>
              </TableCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {hasNoClosedAssessments ? (
              <TableRow>
                <TableCell colSpan="4" align="center">
                  No Closed Assessments
                </TableCell>
              </TableRow>
            ) : (
              <>
                {managerClosedAssessments.map((assessment) => (
                  <TableRow key={assessment.assessment_id}>
                    <TableCell>{assessment.name}</TableCell>
                    <TableCell>Manager</TableCell>
                    <TableCell>
                      {new Date(assessment.due_by).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Button
                        label="View Recommendations"
                        onClick={() =>
                          handleViewResults(assessment, selectedUser)
                        }
                        color="neutral-3"
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {selfAssessments.map((selfAssessment) => (
                  <TableRow key={`self-${selfAssessment.assessment_id}`}>
                    <TableCell>
                      {`Assessment dated: ${new Date(
                        selfAssessment.end_date,
                      ).toLocaleDateString()} - ${selfAssessment.end_time}`}
                    </TableCell>
                    <TableCell>Self</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>
                      <Button
                        label="View Recommendations"
                        onClick={() =>
                          handleViewSelfResults(selfAssessment, selectedUser)
                        }
                        color="neutral-3"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
          </TableBody>
        </Table>
      );
    }
  };

  const inviteCountLookup = selfAssessmentCounts.reduce((acc, cur) => {
    acc[cur.user_id] = cur.completed_assessments;
    return acc;
  }, {});

  return (
    <Box pad="medium" gap="medium">
      {loading && (
        <Layer>
          <Box pad="medium" align="center" gap="small">
            <Spinner />
            <Text>Processing...</Text>
          </Box>
        </Layer>
      )}

      {showSuccess && (
        <Notification
          toast
          status="normal"
          title="Action Successful"
          message="The user was successfully deleted."
          onClose={() => setShowSuccess(false)}
        />
      )}

      {inviteSuccess && (
        <Notification
          toast
          status="normal"
          title="User Invited"
          message="The user was successfully invited."
          onClose={() => setInviteSuccess(false)}
        />
      )}

      {/* Header Section */}
      <Box direction="row" justify="between" align="center" pad="small">
        <Box direction="column">
          <Heading level="2" margin="none">
            Manage Users
          </Heading>
          <Text margin={{ top: "small" }}>
            {invitedUsers.length} users have been invited,{" "}
            {10 - invitedUsers.length} invites remaining.
          </Text>
        </Box>
        <Box direction="row" margin={{ horizontal: "medium" }}>
          <Button
            icon={<Refresh />}
            onClick={() => setRefreshKey((prev) => prev + 1)}
            title="Refresh Users"
            plain={false}
            color="brand"
            margin={{ right: "small" }}
          />
          {/* <SendInvite
            manager_id={manager_id}
            onUserAdded={handleUserAdded}
            users={invitedUsers.length}
          /> */}
        </Box>
      </Box>

      {/* Users Table */}
      <Box overflow="auto" background="light-1" round="small" pad="small">
        <Table>
          <TableHeader>
            <TableRow>
              <TableCell rowSpan={2}>
                <Text weight="bold">User</Text>
              </TableCell>
              <TableCell colSpan={3}>
                <Text weight="bold" alignSelf="center">
                  Assessments
                </Text>
              </TableCell>
              <TableCell rowSpan={2}>
                <Text weight="bold">Invite Status</Text>
              </TableCell>
              <TableCell rowSpan={2} align="center">
                <Text weight="bold">Actions</Text>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Text weight="bold">Open</Text>
              </TableCell>
              <TableCell>
                <Text weight="bold">Closed</Text>
              </TableCell>
              <TableCell>
                <Text weight="bold">Self</Text>
              </TableCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoadingUsers ? (
              <TableRow>
                <TableCell colSpan="5" align="center">
                  <Spinner />
                </TableCell>
              </TableRow>
            ) : invitedUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan="5" align="center">
                  No users invited yet.
                </TableCell>
              </TableRow>
            ) : (
              invitedUsers.map((user) => {
                const openAssessmentsCount = getAssessmentsByUser(
                  user.user_id,
                  0,
                ).length;
                const closedAssessmentsCount = getAssessmentsByUser(
                  user.user_id,
                  1,
                ).length;

                return (
                  <TableRow key={user.user_id}>
                    <TableCell>
                      {user.name}
                      <br />
                      {user.user_email}
                    </TableCell>
                    <TableCell>{openAssessmentsCount}</TableCell>
                    <TableCell>{closedAssessmentsCount}</TableCell>
                    <TableCell>
                      {inviteCountLookup[user.user_id] || 0}
                    </TableCell>
                    <TableCell>
                      {user.invite_status === 0
                        ? "Pending"
                        : user.invite_status === 1
                          ? "Accepted"
                          : user.invite_status === 2
                            ? "Declined"
                            : user.invite_status === 3
                              ? "Active"
                              : "Unknown"}
                    </TableCell>
                    <TableCell>
                      <Box
                        direction="row"
                        gap="xsmall"
                        justify="center"
                        align="center"
                        alignContent="center"
                      >
                        <Button
                          icon={<View />}
                          title="View Assessments"
                          onClick={() => handleViewAssessments(user)}
                          size="large"
                          color="brand"
                        />
                        {user.invite_status === 0 ? (
                          <Button
                            icon={<Mail />}
                            title="Remind to attend the invitation"
                            onClick={() =>
                              handleSendEmailReminder(user.user_id)
                            }
                            color="brand"
                            size="large"
                          />
                        ) : null}
                        {user.invite_status === 3 ||
                          user.invite_status === 1 ? (
                          <Button
                            icon={<Trash />}
                            title="Write to us for deleting user"
                            onClick={() => handleWriteToUs(user.user_email)}
                            color="status-critical"
                            size="large"
                          />
                        ) : (
                          <Button
                            icon={<Trash />}
                            title="Delete"
                            onClick={() =>
                              setDeleteConfirmation({
                                user_id: user.user_id,
                                uuid: user.uuid,
                              })
                            }
                            color="status-critical"
                            size="large"
                          />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Box>

      {/* Assessments Popup */}
      {showPopup && selectedUser && (
        <Layer
          onEsc={() => setShowPopup(false)}
          onClickOutside={() => setShowPopup(false)}
          position="center"
          margin="medium"
        >
          <Box
            pad="medium"
            gap="small"
            width="large"
            height={{ max: "85vh" }}
            overflow="hidden"
            direction="column"
          >
            <Heading level="3" margin="none">
              Assessments for {selectedUser?.user_email}
            </Heading>
            <Box direction="row" gap="medium" margin={{ top: "small" }}>
              <Button
                label="Open Assessments"
                onClick={() => setViewOpenAssessments(true)}
                primary={viewOpenAssessments}
                style={{ height: "40px" }}
              />
              <Button
                label="Closed Assessments"
                onClick={() => setViewOpenAssessments(false)}
                primary={!viewOpenAssessments}
                style={{ height: "40px" }}
              />
            </Box>
            <Box flex overflow="auto" margin={{ top: "medium" }}>
              {renderAssessmentsTable()}
            </Box>
            <Button
              label="Close"
              onClick={() => setShowPopup(false)}
              alignSelf="center"
              margin={{ top: "medium" }}
            />
          </Box>
        </Layer>
      )}

      {/* Results Popup */}
      {showResultsPopup && selectedAssessment && (
        <Layer
          onEsc={() => setShowResultsPopup(false)}
          onClickOutside={() => setShowResultsPopup(false)}
          position="center"
        >
          <Box pad="medium" gap="small" width="large">
            <Heading level="3" margin="none">
              Results for {selectedAssessment?.name}
            </Heading>
            <Text>
              Detailed results for the assessment will be displayed here.
            </Text>
            <Button
              label="Close"
              onClick={() => setShowResultsPopup(false)}
              alignSelf="end"
              margin={{ top: "medium" }}
            />
          </Box>
        </Layer>
      )}

      {/* Error Notification */}
      {error && (
        <Notification
          toast
          status="critical"
          title="Error"
          message={error}
          onClose={() => setError("")}
        />
      )}

      {modal && (
        <Layer
          onEsc={() => setModal(null)}
          onClickOutside={() => setModal(null)}
          modal
          position="center"
        >
          <Box pad="medium" gap="small" width="medium">
            <Text weight="bold" size="large">
              {modal.status === "normal" ? "Success" : "Error"}
            </Text>
            <Text>{modal.message}</Text>
            <Box align="end">
              <Button label="Close" onClick={() => setModal(null)} primary />
            </Box>
          </Box>
        </Layer>
      )}

      {/* Delete Confirmation Layer */}
      {deleteConfirmation && (
        <Layer
          onEsc={() => setDeleteConfirmation(null)}
          onClickOutside={() => setDeleteConfirmation(null)}
        >
          <Box pad="medium" gap="small" width="medium">
            <Heading level={3} margin="none">
              Confirm Deletion
            </Heading>
            <Text>Are you sure you want to delete this user?</Text>
            <Box
              direction="row"
              justify="end"
              gap="small"
              margin={{ top: "medium" }}
            >
              <Button
                label="Cancel"
                onClick={() => setDeleteConfirmation(null)}
                secondary
              />
              <Button
                label="Delete"
                onClick={() => {
                  handleDeleteUser(
                    deleteConfirmation.user_id,
                    deleteConfirmation.uuid,
                  );
                  setDeleteConfirmation(null);
                }}
                primary
                color="status-critical"
              />
            </Box>
          </Box>
        </Layer>
      )}
    </Box>
  );
};

export default ManageUsers;
